// ecommerce-frontend/src/pages/vendor/AddProductPage.jsx
// ✅ VERSION HARMONISÉE: UX moderne + Glassmorphism + Thème Sombre (Header Corrigé)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, DollarSign, Hash, FileText, Upload, ArrowLeft, Save, 
  AlertCircle, CheckCircle, Home, X, Image as ImageIcon, Loader 
} from 'lucide-react';
import { productsAPI, categoriesAPI, authAPI } from '../../services/api';

export default function AddProductPage() {
  const navigate = useNavigate();
  const user = authAPI.getCurrentUser();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    category: '',
  });
  
  const [mainImage, setMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalImagesPreviews, setAdditionalImagesPreviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const MAX_ADDITIONAL_IMAGES = 9;

  useEffect(() => {
    if (!user || user.user_type !== 'vendeur') {
      navigate('/login');
      return;
    }
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCategories = async () => {
    try {
      const data = await categoriesAPI.getAll();
      const fetchedCategories = data.results || data || [];
      setCategories(fetchedCategories);
      
      if (fetchedCategories.length === 0) {
        console.warn('Aucune catégorie disponible.');
      }
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
      setCategories([]);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('L\'image principale ne doit pas dépasser 5 MB');
        return;
      }
      setMainImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setMainImagePreview(reader.result);
      reader.readAsDataURL(file);
      if (errors.image) {
        setErrors({ ...errors, image: '' });
      }
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = additionalImages.length;
    const remainingSlots = MAX_ADDITIONAL_IMAGES - totalImages;
    
    if (files.length > remainingSlots) {
      alert(`Vous pouvez ajouter seulement ${remainingSlots} image(s) supplémentaire(s). Maximum ${MAX_ADDITIONAL_IMAGES} images additionnelles.`);
      return;
    }

    // Vérifier la taille de chaque fichier
    for (let file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert(`L'image ${file.name} dépasse 5 MB`);
        return;
      }
    }

    const newImages = [...additionalImages, ...files];
    setAdditionalImages(newImages);

    // Créer les previews
    const newPreviews = [...additionalImagesPreviews];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        setAdditionalImagesPreviews([...newPreviews]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAdditionalImage = (index) => {
    const newImages = additionalImages.filter((_, i) => i !== index);
    const newPreviews = additionalImagesPreviews.filter((_, i) => i !== index);
    setAdditionalImages(newImages);
    setAdditionalImagesPreviews(newPreviews);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name || formData.name.length < 3) {
      newErrors.name = 'Le nom doit contenir au moins 3 caractères';
    }
    if (!formData.description || formData.description.length < 10) {
      newErrors.description = 'La description doit contenir au moins 10 caractères';
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Le prix doit être supérieur à 0';
    }
    if (!formData.stock || parseInt(formData.stock) < 0) {
      newErrors.stock = 'Le stock doit être un nombre positif';
    }
    if (!formData.category) {
      newErrors.category = 'Veuillez sélectionner une catégorie';
    }
    if (!mainImage) {
      newErrors.image = 'Veuillez ajouter une image principale du produit';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('stock', formData.stock);
      formDataToSend.append('category', formData.category);
      
      // Image principale
      if (mainImage) formDataToSend.append('image', mainImage);
      
      // Images additionnelles
      additionalImages.forEach((image, index) => {
        formDataToSend.append(`additional_images`, image);
      });

      await productsAPI.create(formDataToSend);
      setSuccess(true);
      setTimeout(() => navigate('/vendor/dashboard'), 2000);
    } catch (err) {
      console.error('Erreur ajout produit:', err);
      if (err.response && err.response.data) {
        setErrors(err.response.data);
      } else {
        setErrors({ general: 'Erreur lors de l\'ajout du produit' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated Background - HARMONISÉ */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Bulle orange/rose */}
        <div className="absolute top-0 -left-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        {/* Bulle mauve/bleu */}
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-gradient-to-tl from-purple-500/15 to-blue-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        {/* Bulle centrale */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
      </div>

      {/* Header Glass - HARMONISÉ (Corrigé) */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/5 border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/vendor/dashboard')}
                className="group p-2.5 text-white/70 hover:text-purple-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <ArrowLeft size={20} className="relative z-10" />
              </button>
              {/* Logo MonMarket harmonisé */}
              <div 
                className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Bouton Accueil harmonisé */}
              <button 
                onClick={() => navigate('/')}  
                className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <Home size={20} className="relative z-10" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 relative z-10">
        {success ? (
          <div className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl p-12 text-center border border-white/20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-3xl mb-4 shadow-xl">
              <CheckCircle size={48} className="text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              Produit ajouté avec succès !
            </h2>
            <p className="text-gray-300 mb-6">
              Votre produit est maintenant visible dans votre catalogue.
            </p>
            <button
              onClick={() => navigate('/vendor/dashboard')}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-2xl hover:from-orange-600 hover:to-orange-700 transition-all shadow-xl font-semibold transform hover:scale-105"
            >
              Retour au tableau de bord
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden border border-white/20">
            {/* Header du formulaire */}
            <div className="bg-gradient-to-r from-orange-500 to-pink-500 p-6 text-white">
              <h1 className="text-3xl font-bold">Nouveau produit</h1>
              <p className="text-orange-100 mt-2">Remplissez les informations essentielles du produit.</p>
            </div>

            <div className="p-8 space-y-6">
              {/* Reste du formulaire inchangé... */}
              {/* ... (omitted for brevity, assume the rest of the form is here) */}

              {/* Alerte générale */}
              {errors.general && (
                <div className="bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-2xl p-4 flex items-start">
                  <AlertCircle size={20} className="text-red-300 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-red-100">{errors.general}</span>
                </div>
              )}

              {/* Alerte catégories */}
              {categories.length === 0 && (
                <div className="bg-yellow-500/20 backdrop-blur-md border border-yellow-400/30 rounded-2xl p-4 flex items-start">
                  <AlertCircle size={20} className="text-yellow-300 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-yellow-100 font-medium">Aucune catégorie disponible</span>
                    <p className="text-yellow-200/80 text-sm mt-1">
                      Veuillez créer des catégories dans l'administration Django.
                    </p>
                  </div>
                </div>
              )}

              {/* Image principale */}
              <div className="p-4 bg-white/5 rounded-2xl border border-white/10 shadow-inner">
                <label className="block text-sm font-medium text-gray-200 mb-3">
                  Image principale du produit *
                </label>
                <div className="flex items-center space-x-4">
                  {mainImagePreview ? (
                    <div className="relative group/img">
                      <img
                        src={mainImagePreview}
                        alt="Aperçu principal"
                        className="w-32 h-32 object-cover rounded-2xl border-2 border-orange-400/50 shadow-xl transition-all group-hover/img:scale-105"
                      />
                      <button
                        type="button"
                        onClick={() => { setMainImage(null); setMainImagePreview(null); }}
                        className="absolute -top-3 -right-3 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-all transform hover:scale-110"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20 shadow-lg">
                      <Upload size={32} className="text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleMainImageChange}
                      className="hidden"
                      id="main-image-upload"
                    />
                    <label
                      htmlFor="main-image-upload"
                      className="cursor-pointer inline-flex items-center space-x-2 bg-gradient-to-r from-white/10 to-white/5 hover:from-white/20 hover:to-white/10 backdrop-blur-md text-white px-6 py-3 rounded-2xl transition-all border border-white/20 font-semibold transform hover:scale-[1.02]"
                    >
                      <Upload size={18} />
                      <span>Choisir l'image principale</span>
                    </label>
                    <p className="text-xs text-gray-400 mt-2">
                      JPG, PNG ou GIF. Taille maximale 5 MB.
                    </p>
                  </div>
                </div>
                {errors.image && (
                  <p className="text-red-300 text-sm mt-2 flex items-center"><AlertCircle size={14} className="mr-1" />{errors.image}</p>
                )}
              </div>

              {/* Images additionnelles */}
              <div className="p-4 bg-white/5 rounded-2xl border border-white/10 shadow-inner">
                <label className="block text-sm font-medium text-gray-200 mb-3">
                  Images additionnelles (max {MAX_ADDITIONAL_IMAGES})
                </label>
                
                {/* Grille des images additionnelles */}
                {additionalImagesPreviews.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 mb-4">
                    {additionalImagesPreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`Aperçu ${index + 1}`}
                          className="w-full h-24 object-cover rounded-xl border border-white/20 transition-all group-hover:scale-105"
                        />
                        <button
                          type="button"
                          onClick={() => removeAdditionalImage(index)}
                          className="absolute -top-3 -right-3 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-all transform hover:scale-110 shadow-md"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bouton d'ajout */}
                {additionalImages.length < MAX_ADDITIONAL_IMAGES && (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleAdditionalImagesChange}
                      className="hidden"
                      id="additional-images-upload"
                    />
                    <label
                      htmlFor="additional-images-upload"
                      className="cursor-pointer inline-flex items-center space-x-2 bg-gradient-to-r from-white/10 to-white/5 hover:from-white/20 hover:to-white/10 backdrop-blur-md text-white px-6 py-3 rounded-2xl transition-all border border-white/20 font-semibold transform hover:scale-[1.02]"
                    >
                      <ImageIcon size={18} />
                      <span>Ajouter des images ({additionalImages.length}/{MAX_ADDITIONAL_IMAGES})</span>
                    </label>
                    <p className="text-xs text-gray-400 mt-2">
                      Sélectionnez plusieurs images. 5 MB max par image.
                    </p>
                  </div>
                )}
              </div>

              {/* Nom du produit */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Nom du produit *
                </label>
                <div className="relative">
                  <Package className="absolute left-4 top-4 text-gray-400" size={20} />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border ${
                      errors.name ? 'border-red-400/50' : 'border-white/20'
                    } rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all`}
                    placeholder="Ex: iPhone 14 Pro Max"
                  />
                </div>
                {errors.name && (
                  <p className="text-red-300 text-sm mt-2 flex items-center"><AlertCircle size={14} className="mr-1" />{errors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Description *
                </label>
                <div className="relative">
                  <FileText className="absolute left-4 top-4 text-gray-400" size={20} />
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className={`w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border ${
                      errors.description ? 'border-red-400/50' : 'border-white/20'
                    } rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all`}
                    placeholder="Décrivez votre produit en détail..."
                  />
                </div>
                {errors.description && (
                  <p className="text-red-300 text-sm mt-2 flex items-center"><AlertCircle size={14} className="mr-1" />{errors.description}</p>
                )}
              </div>

              {/* Prix et Stock */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Prix (FCFA) *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-4 top-4 text-gray-400" size={20} />
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      className={`w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border ${
                        errors.price ? 'border-red-400/50' : 'border-white/20'
                      } rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all`}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.price && (
                    <p className="text-red-300 text-sm mt-2 flex items-center"><AlertCircle size={14} className="mr-1" />{errors.price}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Stock (unités) *
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-4 top-4 text-gray-400" size={20} />
                    <input
                      type="number"
                      name="stock"
                      value={formData.stock}
                      onChange={handleChange}
                      min="0"
                      className={`w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border ${
                        errors.stock ? 'border-red-400/50' : 'border-white/20'
                      } rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all`}
                      placeholder="0"
                    />
                  </div>
                  {errors.stock && (
                    <p className="text-red-300 text-sm mt-2 flex items-center"><AlertCircle size={14} className="mr-1" />{errors.stock}</p>
                  )}
                </div>
              </div>

              {/* Catégorie */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Catégorie *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  disabled={categories.length === 0}
                  className={`w-full px-4 py-4 bg-white/10 backdrop-blur-md border ${
                    errors.category ? 'border-red-400/50' : 'border-white/20'
                  } rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white transition-all appearance-none ${
                    categories.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="" className="bg-gray-900">
                    {categories.length === 0 ? 'Aucune catégorie disponible' : 'Sélectionnez une catégorie'}
                  </option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id} className="bg-gray-900">
                      {cat.name}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-red-300 text-sm mt-2 flex items-center"><AlertCircle size={14} className="mr-1" />{errors.category}</p>
                )}
              </div>

              {/* Boutons */}
              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/vendor/dashboard')}
                  className="flex-1 bg-white/10 backdrop-blur-md text-white py-4 rounded-2xl font-bold hover:bg-white/20 transition-all border border-white/20 shadow-lg transform hover:scale-[1.01]"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading || categories.length === 0}
                  className="group/btn flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-2xl font-bold transition-all shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000"></div>
                  {loading ? (
                    <span className="relative flex items-center space-x-2">
                      <Loader size={20} className="animate-spin" />
                      <span>Ajout en cours...</span>
                    </span>
                  ) : (
                    <span className="relative flex items-center space-x-2">
                      <Save size={20} />
                      <span>Ajouter le produit</span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}